/**
 * Client half of the widget configuration contract (WidgetConfigV1).
 *
 * The SERVER owns the contract: aiva.api `Services/Dashboard/WidgetConfig/` holds the typed model,
 * the v0 -> v1 upgrader and the write-time validator, and serves the JSON Schema at
 * `GET api/DashboardWidgets/config-schema`. This module only does the three things the wizard
 * itself must get right:
 *   1. stamp the version it writes (WIDGET_CONFIG_SCHEMA_VERSION);
 *   2. carry over the v1 keys it does not own, so a config written by another client (e.g. the
 *      AVA MCP) survives a UI edit instead of being silently dropped by buildWidgetPayload's fixed
 *      list - and drop every key outside v1, which the server would reject as unknown;
 *   3. never send a setting the server forbids for the widget type while the wizard hides it
 *      (a count/gauge group-by, a sort on a deselected column), since the user could not fix it;
 *   4. turn a rejected save (400 WIDGET_CONFIG_INVALID) into readable messages.
 *
 * PURE: no Vue, no store, no I/O.
 */

/** Version of the widget config the wizard writes. Must match WidgetConfigV1.CurrentSchemaVersion. */
export const WIDGET_CONFIG_SCHEMA_VERSION = 1;

/** Root code of the 400 body the API returns when it rejects a widget config. */
export const WIDGET_CONFIG_INVALID = 'WIDGET_CONFIG_INVALID';

/**
 * Every sqlViewConfig key EditSqlViewWidget.buildWidgetPayload writes, always or conditionally.
 * The wizard decides these on every save, including deciding to OMIT one (for example
 * `comparison` after switching away from a comparison chart, or the POI keys under binding
 * focus), so an owned key is never carried over from the loaded config.
 */
export const WIZARD_OWNED_CONFIG_KEYS = Object.freeze([
  'schemaVersion',
  'tableName', 'tableDisplayName', 'columns', 'columnTypes', 'columnDisplayNames',
  'aggregation', 'stackMode', 'topBottom', 'sort', 'axisByMetric', 'seriesStyles', 'comparison',
  'filters', 'link3D', 'tagColumn', 'tagLink', 'spatialFocus', 'coloring', 'columnColoring',
  'poiSource', 'contextSource', 'poiPaintMode', 'highlightColorSource',
  'refreshIntervalMinutes', 'exportEnabled', 'columnWidthMode', 'aboutText', 'widgetSize',
  'icon', 'unit', 'footnote', 'kpiVariant', 'gauge', 'tone', 'thresholds',
  'coloringSource', 'schemaSelection', 'sourceKey', 'createdFromWidgetId',
]);

/**
 * Every top-level property of WidgetConfigV1 (aiva.api `Services/Dashboard/WidgetConfig/
 * WidgetConfigV1.cs`, camelCased as the API serialises it). The server rejects any other key.
 * A static list rather than a fetch of GET api/DashboardWidgets/config-schema: the wizard needs it
 * synchronously when a widget opens, and widgetConfigContract.test.js checks it against the C# model.
 */
export const WIDGET_CONFIG_V1_KEYS = Object.freeze([
  'schemaVersion',
  'tableName', 'tableDisplayName', 'columns', 'columnTypes', 'columnDisplayNames',
  'aggregation', 'filters', 'sort', 'topBottom', 'take',
  'aboutText', 'widgetSize', 'icon', 'unit', 'footnote', 'centerLabel', 'kpiVariant', 'gauge', 'tone',
  'thresholds', 'coloringSource', 'schemaSelection', 'stackMode', 'axisByMetric', 'seriesStyles',
  'comparison', 'columnColoring', 'exportEnabled', 'columnWidthMode', 'refreshIntervalMinutes',
  'link3D', 'tagColumn', 'tagLink', 'spatialFocus', 'coloring', 'poiSource', 'contextSource',
  'poiPaintMode', 'highlightColorSource', 'listDrill',
  'sourceKey', 'createdFromWidgetId',
]);

const OWNED = new Set(WIZARD_OWNED_CONFIG_KEYS);

/** The v1 keys the wizard does not write itself and therefore carries over from the loaded config. */
export const PRESERVED_CONFIG_KEYS = Object.freeze(WIDGET_CONFIG_V1_KEYS.filter(key => !OWNED.has(key)));

const PRESERVED = new Set(PRESERVED_CONFIG_KEYS);

/**
 * The entries of a loaded (parsed) sqlViewConfig that the wizard does not own and must carry
 * over on save: v1 keys only, so a legacy row's stray keys are dropped instead of failing the save
 * as unknown. Returns a new object; the input is not modified.
 * @param {object} loadedConfig - the parsed sqlViewConfig the wizard was opened with
 * @returns {object}
 */
export function preservedConfigEntries(loadedConfig) {
  if (!loadedConfig || typeof loadedConfig !== 'object' || Array.isArray(loadedConfig)) return {};
  return Object.fromEntries(Object.entries(loadedConfig)
    .filter(([key]) => PRESERVED.has(key)));
}

/**
 * The config to save: the carried-over entries plus what the wizard built. The wizard's values
 * always win. Returns a new object.
 * @param {object} builtConfig - the sqlViewConfig object buildWidgetPayload assembled
 * @param {object} preserved - from preservedConfigEntries
 * @returns {object}
 */
export function withPreservedEntries(builtConfig, preserved) {
  return { ...(preserved || {}), ...builtConfig };
}

/**
 * The carried-over entries that still fit the source being saved. `listDrill.column` names a column
 * of the source it was set on, and the server rejects a column the source lacks
 * (COLUMN_NOT_FOUND) while the wizard offers no control to remove it, so it is dropped when the
 * user switched the widget to another table, or when the loaded column list no longer has it. On a
 * new table it could also name a same-spelled column of a different entity (every composite source
 * has an Id), so a table switch drops it even when the column exists. Column names compare
 * case-insensitively, as on the server. Returns a new object; the input is not modified.
 * @param {object} preserved - from preservedConfigEntries
 * @param {{ loadedTableName?: string, tableName?: string, columnNames?: string[] }} source -
 *   the table the wizard was opened on, the table being saved, and its loaded column names
 *   (an empty list means "not loaded" and keeps the entry)
 * @returns {object}
 */
export function preservedEntriesForSource(preserved, { loadedTableName, tableName, columnNames } = {}) {
  const entries = { ...(preserved || {}) };
  const drillColumn = entries.listDrill && entries.listDrill.column;
  if (!drillColumn) return entries;
  const lower = value => String(value || '').toLowerCase();
  const tableChanged = !!loadedTableName && lower(loadedTableName) !== lower(tableName);
  const names = Array.isArray(columnNames) ? columnNames.map(lower) : [];
  const columnGone = names.length > 0 && !names.includes(lower(drillColumn));
  if (tableChanged || columnGone) delete entries.listDrill;
  return entries;
}

/**
 * The aggregation to store for a widget type. A number (KPI) or gauge widget - messageType
 * 'count' - shows a single total: the server rejects any group-by or difference bucket
 * (GROUP_BY_NOT_ALLOWED) and the wizard hides both for that type, so they are left out rather than
 * sent. The working config keeps them, so switching back to a chart restores the grouping. Other
 * types get the same object back. Returns a new object when it strips; the input is not modified.
 * @param {object} aggregation - { groupBy, metrics, differenceBucketGroup? }
 * @param {string} messageType - the widget's display type
 * @returns {object}
 */
export function aggregationForType(aggregation, messageType) {
  if (messageType !== 'count' || !aggregation || typeof aggregation !== 'object') return aggregation;
  return Object.fromEntries([
    ...Object.entries(aggregation).filter(([key]) => key !== 'differenceBucketGroup'),
    ['groupBy', []],
  ]);
}

/**
 * The default sort to store: null when its column is no longer one of the selected columns (the
 * sort picker only lists selected columns, so the user could not see or fix it, and the server
 * rejects it with SORT_COLUMN_NOT_ALLOWED). Column names compare case-insensitively, as on the server.
 * @param {{ column: string, direction: string } | null} sort
 * @param {string[]} columns - the columns the widget queries
 * @returns {{ column: string, direction: string } | null}
 */
export function sortForColumns(sort, columns) {
  if (!sort || !sort.column) return sort || null;
  const wanted = String(sort.column).toLowerCase();
  return (columns || []).some(column => String(column || '').toLowerCase() === wanted) ? sort : null;
}

/**
 * The filters to store: an "is one of" filter with nothing picked is dropped. The query builder
 * always ignored it (it would match every row), and the server now rejects it with
 * FILTER_IN_VALUES, so dropping it keeps the save working exactly as before. "in_or_null" with no
 * values is kept: it means "only empty values". Returns a new array; the input is not modified.
 * @param {Array<{ column: string, operator: string, value: * }>} filters
 * @returns {Array}
 */
export function filtersToStore(filters) {
  return (filters || []).filter(filter => !(filter && filter.operator === 'in' && isEmptyFilterValue(filter.value)));
}

function isEmptyFilterValue(value) {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.every(item => item === null || item === undefined || String(item).trim() === '');
  const text = String(value).trim();
  return text === '' || text === '[]';
}

/**
 * Readable messages for a failed widget save. A contract rejection lists one message per problem
 * (the API writes them for end users); anything else falls back to the server's message or a
 * generic line.
 * @param {*} err - the axios error
 * @returns {string[]}
 */
export function widgetSaveErrorMessages(err) {
  const data = err && err.response ? err.response.data : null;
  if (data && data.code === WIDGET_CONFIG_INVALID && Array.isArray(data.errors) && data.errors.length) {
    const messages = data.errors
      .map(problem => (problem && typeof problem.message === 'string' ? problem.message.trim() : ''))
      .filter(Boolean);
    if (messages.length) return messages;
  }
  if (typeof data === 'string' && data.trim()) return [data.trim()];
  if (data && typeof data.message === 'string' && data.message.trim()) return [data.message.trim()];
  return ['The widget could not be saved. Please try again.'];
}
